'use client';

import { useSession } from 'next-auth/react';
import { useEffect, useRef, useState, useTransition } from 'react';

import { LikeButtonLayout } from './layout';
import { toggleArticleReaction } from './actions';

type LikeButtonClientProps = {
	articleId: string;
	initialReactedUsers: string[];
	initialUserId?: string | null;
};

export const LikeButtonClient = ({
	articleId,
	initialReactedUsers,
	initialUserId = null,
}: LikeButtonClientProps) => {
	const { data: session, status } = useSession();
	const sessionUserId = (session?.user as any)?.id as string | undefined;
	const effectiveUserId = sessionUserId ?? initialUserId ?? null;

	const [liked, setLiked] = useState<boolean>(() =>
		Boolean(initialUserId && initialReactedUsers.includes(initialUserId))
	);
	const [likeCount, setLikeCount] = useState<number>(initialReactedUsers.length);
	const [isPending, startTransition] = useTransition();

	const previousUserIdRef = useRef<string | null | undefined>(initialUserId ?? null);

	// Sync liked state when the authenticated user changes (e.g. login/logout)
	useEffect(() => {
		if (previousUserIdRef.current === effectiveUserId) {
			return;
		}

		previousUserIdRef.current = effectiveUserId;
		if (!effectiveUserId) {
			setLiked(false);
			return;
		}

		setLiked(initialReactedUsers.includes(effectiveUserId));
	}, [effectiveUserId, initialReactedUsers]);

	// Ensure we initialize once session loading completes if we didn't have a user id
	useEffect(() => {
		if (status === 'loading') {
			return;
		}

		if (!previousUserIdRef.current && effectiveUserId) {
			previousUserIdRef.current = effectiveUserId;
			setLiked(initialReactedUsers.includes(effectiveUserId));
		}
	}, [status, effectiveUserId, initialReactedUsers]);

	const handleToggle = () => {
		if (!effectiveUserId || isPending) {
			return;
		}

		const prevLiked = liked;
		const prevCount = likeCount;
		const nextLiked = !liked;

		setLiked(nextLiked);
		setLikeCount((current) => Math.max(0, current + (nextLiked ? 1 : -1)));

		startTransition(async () => {
			const result = await toggleArticleReaction(articleId);

			if (!result || result.error) {
				setLiked(prevLiked);
				setLikeCount(prevCount);
				return;
			}

			setLiked(result.liked);
			setLikeCount(result.count);
		});
	};

	return (
		<LikeButtonLayout
			liked={liked}
			disabled={!effectiveUserId}
			likeCount={likeCount}
			isPending={isPending}
			onToggle={handleToggle}
		/>
	);
};
